import {useCallback, useEffect, useRef, useState} from 'react';

const HEADWAY_SCRIPT_URL = 'https://cdn.headwayapp.co/widget.js';
const HEADWAY_ACCOUNT = process.env.REACT_APP_HEADWAY_ACCOUNT_ID;
const CONTAINER_ID = 'headway-hidden-container';

let scriptLoaded = false;

function loadScript() {
  return new Promise((resolve, reject) => {
    if (scriptLoaded || window.Headway) {
      scriptLoaded = true;
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = HEADWAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      scriptLoaded = true;
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function ensureContainer() {
  let el = document.getElementById(CONTAINER_ID);
  if (el) return el;
  el = document.createElement('div');
  el.id = CONTAINER_ID;
  el.style.cssText =
    'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;overflow:hidden;';
  document.body.appendChild(el);
  return el;
}

/**
 * Loads the Headway widget off-screen and exposes the unseen changelog count.
 * Call markSeen() when the user views the changelog — this triggers Headway's
 * native show/hide cycle to persist the "last seen" timestamp in its cookie.
 *
 * Requires REACT_APP_HEADWAY_ACCOUNT_ID to be set in the environment.
 */
export function useHeadwayWidget() {
  const [unseenCount, setUnseenCount] = useState(0);
  const initialized = useRef(false);

  useEffect(() => {
    if (!HEADWAY_ACCOUNT || initialized.current) return;

    let cancelled = false;

    loadScript().then(() => {
      if (cancelled || !window.Headway) return;
      initialized.current = true;

      ensureContainer();

      window.Headway.init({
        selector: `#${CONTAINER_ID}`,
        account: HEADWAY_ACCOUNT,
        callbacks: {
          onWidgetReady: (widget) => {
            if (!cancelled) setUnseenCount(widget.getUnseenCount());
          },
          onShowWidget: () => setUnseenCount(0),
        },
      });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const markSeen = useCallback(() => {
    setUnseenCount(0);
    if (window.Headway) {
      window.Headway.show();
    }
  }, []);

  return {unseenCount, markSeen};
}
